import { trpc } from "@blis/trpc/react";

import { apiUtils } from "@/context/query";
import { useAuth } from "@/features/auth/hooks";
import { rt, useRealtimeInvalidate } from "@/lib/realtime";
import { useOptimisticIds } from "@/lib/useOptimisticIds";

const invalidateRequiringReports = () =>
  apiUtils.accession.requiringReports.invalidate();

/** Per-user sessionStorage key for the optimistic "Generating…" set. */
const ssKeyFor = (userId: number | undefined) =>
  userId != null ? `blis:pendingReportSubmissions:user-${userId}` : null;

/**
 * Backs the sidebar pending-reports badge and the Reports page list.
 *
 * Two pieces of plumbing live behind the surface:
 *
 * 1. The realtime transport emits the coarse `accessions` tag whenever an
 *    accession-adjacent write lands, which keeps this aggregate query fresh.
 * 2. {@link useOptimisticIds} keeps a per-user, sessionStorage-backed
 *    set of "submitted for generation" accession ids so the UI can
 *    show a "Generating…" pill that survives refreshes and clears
 *    automatically when the row drops out of `requiringReports`.
 */
export function usePendingReports(enabled = true) {
  const { user } = useAuth();
  const ssKey = ssKeyFor(user?.profile?.id);

  useRealtimeInvalidate({
    tags: [rt.accessions()],
    targets: [invalidateRequiringReports],
    throttleMs: 1000,
    enabled,
  });

  const { data, isLoading } = trpc.accession.requiringReports.useQuery(
    undefined,
    { enabled, trpc: { context: { skipBatch: true } } },
  );

  const { ids: queuedIds, add: markSubmitted } = useOptimisticIds(ssKey, data);

  return {
    accessions: data ?? [],
    queuedIds,
    fetching: isLoading,
    markSubmitted,
  };
}
